import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Tooltip } from '@modusign/design-system';
import {
  useStartSubscriptionWithTrialPlan,
  useValidateAvailableTrialPlan,
} from '@modusign/openapi-client-subscription';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import LoadingStatus from '@libs/loading-status';

import { useGNBFoldHandle } from '@app/containers/GlobalNavigationBar/GNBFoldHandleContext';
import { PLAN_TRIAL } from '@app/features/subscription/constants';
import {
  getSubscriptionLoadingStatus,
  getSubscriptionPlan,
  getSubscriptionStatus,
} from '@app/features/subscription/state';
import SubscriptionStatus from '@app/features/subscription/types/SubscriptionStatus';
import { useColorScheme } from '@app/hooks/useColorScheme';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useWorkspace } from '@app/hooks/workspace';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { modalOpen } from '@app/store/modal/actions';
import {
  getPermissionsAndLimitsLoadingStatus,
  getUserAuthorityLoadingStatus,
} from '@app/store/user/authority/selectors';
import { getUserLoadingStatus } from '@app/store/user/user/selectors';

import ContractSetupStartButton from './ContractSetupStartButton';
import GNBPrimaryButton from './GNBPrimaryButton';
import RemainingSigningRequestCount from './RemainingSigningRequestCount';
import SigningRequestUploadModal from './SigningRequestUploadModal';

function GNBSubHeader({ hasMenuSectionScrolled }: { hasMenuSectionScrolled?: boolean }) {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const shouldDisplayRemainingCountGauge = useShouldDisplayRemainingCountGauge();
  const { theme } = useColorScheme();
  const { folded } = useGNBFoldHandle();

  return (
    <Flex
      alignItems={folded ? 'center' : 'initial'}
      borderBottom="1px solid"
      borderBottomColor={hasMenuSectionScrolled ? theme.gnb.dividerColor : 'transparent'}
      flexDirection="column"
      gap="10px"
      pb={4}
      pt={folded ? 4 : 7}
      px={folded ? 0 : 5}
    >
      <GNBSubHeaderPrimeActionButton onClick={() => setIsUploadModalOpen(true)} />
      {!folded && shouldDisplayRemainingCountGauge && <RemainingSigningRequestCount />}
      {isUploadModalOpen && (
        <SigningRequestUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
        />
      )}
    </Flex>
  );
}

function GNBSubHeaderPrimeActionButton({ onClick }: { onClick: () => void }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { signingRequestCount } = useUsageCount();
  const isDesktopWidth = useResponsiveValue([false, false, true]);
  const workspace = useWorkspace();
  const {
    mutate,
    data: validateAvailableTrialPlan,
    isLoading: isValidateAvailableTrialPlanFetchLoading,
  } = useValidateAvailableTrialPlan();

  const subscriptionPlan = useSelector(getSubscriptionPlan);
  const userPermissionsAndLimitsLoadingStatus = useSelector(getPermissionsAndLimitsLoadingStatus);
  const userAuthorityLoadingStatus = useSelector(getUserAuthorityLoadingStatus);
  const subscriptionLoadingStatus = useSelector(getSubscriptionLoadingStatus);
  const userLoadingStatus = useSelector(getUserLoadingStatus);
  const subscriptionStatus = useSelector(getSubscriptionStatus);

  const { folded } = useGNBFoldHandle();

  const alertDeviceWidth = useCallback(
    () =>
      dispatch(
        modalOpen('CONFIRM', {
          title: '이용 안내',
          content:
            '화면 크기가 작아 해당 기능을 이용할 수 없습니다.\n더 큰 화면의 PC에서 이용하시길 권장합니다.',
        })
      ),
    [dispatch]
  );

  useEffect(() => {
    if (workspace.id) {
      mutate({ workspaceId: workspace.id });
    }
  }, [mutate, workspace.id]);

  if (
    userAuthorityLoadingStatus === LoadingStatus.PENDING ||
    userPermissionsAndLimitsLoadingStatus === LoadingStatus.PENDING ||
    subscriptionLoadingStatus.fetch === LoadingStatus.PENDING ||
    userLoadingStatus === LoadingStatus.PENDING ||
    isValidateAvailableTrialPlanFetchLoading
  ) {
    return folded ? (
      <Button borderRadius="100%" height={56} isLoading weight="light" width={56} />
    ) : (
      <Button isLoading weight="light" />
    );
  }

  if (!workspace.active) {
    return <ContractSetupStartButton onClick={isDesktopWidth ? onClick : alertDeviceWidth} />;
  }

  if (
    validateAvailableTrialPlan?.isAvailableTrialPlan &&
    (location.pathname === '/' || location.pathname === '/drafts')
  ) {
    return <StartTrialButton />;
  }

  if (!subscriptionPlan) {
    return <PlanPurchaseButton>요금제 구매</PlanPurchaseButton>;
  }

  if (subscriptionStatus === SubscriptionStatus.READY) {
    return <PlanPurchaseButton subscriptionReady>결제하고 시작</PlanPurchaseButton>;
  }

  return signingRequestCount.limit - signingRequestCount.usage > 0 ? (
    <ContractSetupStartButton onClick={isDesktopWidth ? onClick : alertDeviceWidth} />
  ) : (
    <SigningRequestLimitPurchaseButton />
  );
}

function StartTrialButton() {
  const dispatch = useDispatch();
  const history = useHistory();
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();
  const { folded } = useGNBFoldHandle();

  const { mutate: startTrial, isLoading: isStartTrialLoading } = useStartSubscriptionWithTrialPlan({
    mutation: {
      onSuccess: () => {
        pushDataLayer({ event: 'getTrial' });
        history.go(0);
      },
      onError: () => {
        createErrorHandler({ title: '무료 체험 시작에 실패했습니다.' });
      },
    },
  });

  const handleClick = () => {
    pushAdobeDataLayer({ event: 'clickShowTrialModal' });
    dispatch(
      modalOpen('CONFIRM', {
        title: '모두싸인 무료 체험 안내',
        content: (
          <Box lineHeight={1.5}>
            <Box>
              서명 요청·템플릿·대량전송 및 전문가가 작성한 법률 서식 등 모든 기능을 무료 이용해
              보세요.
            </Box>
            <Box
              backgroundColor="lightGray50"
              borderColor="lightGray300"
              borderStyle="solid"
              borderWidth="1px"
              mt="18px"
              px="18px"
              py={7}
              textAlign="left"
            >
              30일간 모든 기능이 무료로 제공됩니다.
              <br />
              이후 자동 결제되지 않으니 안심하세요.
            </Box>
          </Box>
        ),
        cancelText: '취소',
        closeText: '30일 무료 체험',
        onClose: () => {
          if (isStartTrialLoading) {
            return;
          }
          startTrial({ workspaceId, data: { planId: PLAN_TRIAL } });
        },
      })
    );
  };

  if (folded) {
    return (
      <Tooltip
        content={
          <Box fontSize="13px" fontWeight="normal" lineHeight={1.5}>
            언제든지 해당 버튼으로 아래 모든 기능을
            <br />
            30일동안 마음껏 이용해보실 수 있어요.
          </Box>
        }
        gutter={26}
        placement="right"
      >
        <Button
          borderRadius="100%"
          height={56}
          variant="primary"
          weight="light"
          width={56}
          onClick={handleClick}
        >
          체험
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={
        <Box fontSize="13px" fontWeight="normal" lineHeight={1.5}>
          언제든지 해당 버튼으로 아래 모든 기능을
          <br />
          30일동안 마음껏 이용해보실 수 있어요.
        </Box>
      }
      gutter={26}
      placement="right"
    >
      <Button variant="primary" weight="light" onClick={handleClick}>
        무료 체험 신청
      </Button>
    </Tooltip>
  );
}

function SigningRequestLimitPurchaseButton() {
  const history = useHistory();

  return (
    <GNBPrimaryButton onClick={() => history.push('/settings/plan')}>
      <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
      사용량 충전
    </GNBPrimaryButton>
  );
}

function PlanPurchaseButton({
  children,
  subscriptionReady,
}: {
  children: string;
  subscriptionReady?: boolean;
}) {
  const history = useHistory();

  const { folded } = useGNBFoldHandle();

  if (folded) {
    return (
      <Button
        borderRadius="100%"
        height={56}
        variant={subscriptionReady ? 'primary' : 'danger'}
        weight="light"
        width={56}
        onClick={() => history.push('/settings/plan')}
      >
        결제
      </Button>
    );
  }

  return (
    <Button
      variant={subscriptionReady ? 'primary' : 'danger'}
      weight="light"
      onClick={() => history.push('/settings/plan')}
    >
      {children}
    </Button>
  );
}

function useShouldDisplayRemainingCountGauge() {
  const subscriptionPlan = useSelector(getSubscriptionPlan);
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  const workspace = useWorkspace();
  const { isPermitted: ableToRequestSigning } = useMemberPermissions('CREATE_DOCUMENT');

  const userIndividualWhoHasPlan = !!subscriptionPlan;

  return (
    userIndividualWhoHasPlan &&
    ableToRequestSigning &&
    subscriptionStatus !== SubscriptionStatus.READY &&
    // avtive workspace에서만 서명 요청 할 수 있습니다.
    workspace.active
  );
}

export default GNBSubHeader;
